import { Injectable } from '@angular/core';
import { filter, Subject, Subscription } from 'rxjs';
import { CompletionMessage, FileUpload } from '@app-libs/model';

export enum EventTypes {
  REMOVE_UPLOADED_FILE = 'REMOVE_UPLOADED_FILE',
  FILE_UPLOADED = 'FILE_UPLOADED',
  SEND_USER_MESSAGE = 'SEND_USER_MESSAGE',
  UPDATE_SELECTED_MODEL = 'UPDATE_SELECTED_MODEL',
  STREAM_FIRST_CHUNK_RECEIVED = 'STREAM_CHUNK_RECEIVED',
  STREAM_ERROR_RECEIVED = 'STREAM_ERROR_RECEIVED',
  STREAM_COMPLETE = 'STREAM_COMPLETE',
  SCROLL_BOTTOM = 'SCROLL_BOTTOM',
  SCROLL_POSITION_CHANGED = 'SCROLL_POSITION_CHANGED',
  UPDATE_DEFAULT_MODEL = 'UPDATE_DEFAULT_MODEL',
  STOP_STREAM = 'STOP_STREAM',
  NEW_CHAT = 'NEW_CHAT',
  WORKSPACE_UPDATED = 'WORKSPACE_UPDATED',
  CHAT_INITIALIZED = 'CHAT_INITIALIZED',
}

// Typed event data interfaces
export interface RemoveUploadedFileEvent {
  type: EventTypes.REMOVE_UPLOADED_FILE;
  data?: any;
}

export interface FileUploadedEvent {
  type: EventTypes.FILE_UPLOADED;
  data: FileUpload;
}

export interface SendUserMessageEvent {
  type: EventTypes.SEND_USER_MESSAGE;
  data: CompletionMessage | Partial<CompletionMessage>;
}

export interface UpdateSelectedModelEvent {
  type: EventTypes.UPDATE_SELECTED_MODEL;
  data: string | any;
}

export interface StreamFirstChunkReceivedEvent {
  type: EventTypes.STREAM_FIRST_CHUNK_RECEIVED;
  data: { content: string; messageId?: string } | any;
}

export interface StreamErrorReceivedEvent {
  type: EventTypes.STREAM_ERROR_RECEIVED;
  data: { error: Error; messageId?: string } | any;
}

export interface StreamCompleteEvent {
  type: EventTypes.STREAM_COMPLETE;
  data: { messageId?: string; usage?: { total_tokens: number; completion_tokens: number; prompt_tokens: number } } | any;
}

export interface ScrollBottomEvent {
  type: EventTypes.SCROLL_BOTTOM;
  data: boolean; // force scroll
}

export interface ScrollPositionChangedEvent {
  type: EventTypes.SCROLL_POSITION_CHANGED;
  data: { isAtBottom: boolean; scrollTop: number; scrollHeight: number };
}

export interface UpdateDefaultModelEvent {
  type: EventTypes.UPDATE_DEFAULT_MODEL;
  data: string;
}

export interface StopStreamEvent {
  type: EventTypes.STOP_STREAM;
  data?: void;
}

export interface NewChatEvent {
  type: EventTypes.NEW_CHAT;
  data?: { assistantId?: string };
}

export interface WorkspaceUpdatedEvent {
  type: EventTypes.WORKSPACE_UPDATED;
  data: { workspaceId: string; workspaceName?: string };
}

export interface ChatInitializedEvent {
  type: EventTypes.CHAT_INITIALIZED;
  data: { assistant: any };
}

// Union type for all possible events
export type EventData = 
  | RemoveUploadedFileEvent
  | FileUploadedEvent
  | SendUserMessageEvent
  | UpdateSelectedModelEvent
  | StreamFirstChunkReceivedEvent
  | StreamErrorReceivedEvent
  | StreamCompleteEvent
  | ScrollBottomEvent
  | ScrollPositionChangedEvent
  | UpdateDefaultModelEvent
  | StopStreamEvent
  | NewChatEvent
  | WorkspaceUpdatedEvent
  | ChatInitializedEvent;

@Injectable({
  providedIn: 'root',
})
export class EventBusService {
  private eventSubject = new Subject<EventData>();

  emit(event: EventData): void {
    // Validate event data structure
    if (!this.validateEvent(event)) {
      console.warn('Invalid event data:', event);
      return;
    }
    
    this.eventSubject.next(event);
  }

  on<T extends EventData>(eventType: EventTypes) {
    return this.eventSubject.asObservable().pipe(
      filter((event): event is T => event.type === eventType)
    );
  }

  subscribeToEvent<T extends EventData>(
    eventType: EventTypes,
    handler: (data: T['data']) => void
  ): Subscription {
    return this.on<T>(eventType).subscribe((event) => handler(event.data));
  }

  // Event validation
  private validateEvent(event: EventData): boolean {
    if (!event || typeof event !== 'object') {
      return false;
    }

    if (!event.type || typeof event.type !== 'string') {
      return false;
    }

    // Check if event type is valid
    if (!Object.values(EventTypes).includes(event.type as EventTypes)) {
      return false;
    }

    return true;
  }

  // Namespaced event emission for better organization
  emitWithNamespace(namespace: string, event: EventData): void {
    const namespacedEvent = {
      ...event,
      namespace,
      originalType: event.type,
      type: `${namespace}:${event.type}` as EventTypes
    };
    
    this.emit(namespacedEvent as EventData);
  }

  // Subscribe to namespaced events
  onNamespaced<T extends EventData>(namespace: string, eventType: EventTypes) {
    const namespacedType = `${namespace}:${eventType}`;
    return this.eventSubject.asObservable().pipe(
      filter((event): event is T => event.type === namespacedType)
    );
  }

  // Typed emit methods with proper data validation
  emitRemoveUploadedFile(data?: any): void {
    this.eventSubject.next({ type: EventTypes.REMOVE_UPLOADED_FILE, data });
  }

  emitFileUploaded(data: FileUpload): void {
    this.eventSubject.next({ type: EventTypes.FILE_UPLOADED, data });
  }

  emitSendUserMessage(data: CompletionMessage | Partial<CompletionMessage>): void {
    this.eventSubject.next({ type: EventTypes.SEND_USER_MESSAGE, data });
  }

  emitUpdateSelectedModel(data: string): void {
    this.eventSubject.next({ type: EventTypes.UPDATE_SELECTED_MODEL, data });
  }

  emitStreamFirstChunkReceived(data: { content: string; messageId?: string } | any): void {
    this.eventSubject.next({
      type: EventTypes.STREAM_FIRST_CHUNK_RECEIVED,
      data,
    });
  }

  emitStreamErrorReceived(data: { error: Error; messageId?: string } | any): void {
    this.eventSubject.next({ type: EventTypes.STREAM_ERROR_RECEIVED, data });
  }

  emitStreamComplete(data: { messageId?: string; usage?: { total_tokens: number; completion_tokens: number; prompt_tokens: number } } | any): void {
    this.eventSubject.next({ type: EventTypes.STREAM_COMPLETE, data });
  }

  emitScrollBottom(force = false): void {
    this.eventSubject.next({ type: EventTypes.SCROLL_BOTTOM, data: force });
  }

  emitScrollPositionChanged(data: { isAtBottom: boolean; scrollTop: number; scrollHeight: number }): void {
    this.eventSubject.next({ type: EventTypes.SCROLL_POSITION_CHANGED, data });
  }

  emitUpdateDefaultModel(data: string): void {
    this.eventSubject.next({ type: EventTypes.UPDATE_DEFAULT_MODEL, data });
  }

  emitStopStream(): void {
    this.eventSubject.next({ type: EventTypes.STOP_STREAM });
  }

  emitNewChat(data?: { assistantId?: string }): void {
    this.eventSubject.next({ type: EventTypes.NEW_CHAT, data });
  }

  emitWorkspaceUpdated(data: { workspaceId: string; workspaceName?: string }): void {
    this.eventSubject.next({ type: EventTypes.WORKSPACE_UPDATED, data });
  }

  // Legacy methods for backward compatibility (deprecated)
  /** @deprecated Use emitRemoveUploadedFile() instead */
  sendRemoveUploadedFile(data?: any) {
    this.emitRemoveUploadedFile(data);
  }

  /** @deprecated Use emitFileUploaded() instead */
  sendFileUploaded(data?: any) {
    if (data) this.emitFileUploaded(data);
  }

  /** @deprecated Use emitSendUserMessage() instead */
  sendUserMessage(data?: any) {
    if (data) this.emitSendUserMessage(data);
  }

  /** @deprecated Use emitUpdateSelectedModel() instead */
  sendUpdateSelectedModel(data?: any) {
    if (data) this.emitUpdateSelectedModel(data);
  }

  /** @deprecated Use emitStreamFirstChunkReceived() instead */
  sendStreamFirstChunkReceived(data?: any) {
    if (data) this.emitStreamFirstChunkReceived(data);
  }

  /** @deprecated Use emitStreamErrorReceived() instead */
  sendStreamErrorReceived(data?: any) {
    if (data) this.emitStreamErrorReceived(data);
  }

  /** @deprecated Use emitStreamComplete() instead */
  sendStreamComplete(data?: any) {
    if (data) this.emitStreamComplete(data);
  }

  /** @deprecated Use emitScrollBottom() instead */
  sendScrollBottom(data?: any) {
    this.emitScrollBottom(data || false);
  }

  /** @deprecated Use emitUpdateDefaultModel() instead */
  sendUpdateDefaultModel(data?: any) {
    if (data) this.emitUpdateDefaultModel(data);
  }

  /** @deprecated Use emitWorkspaceUpdated() instead */
  sendWorkspaceUpdated(data?: any) {
    if (data) this.emitWorkspaceUpdated(data);
  }
}
